import { Directive, ElementRef, HostListener, Inject, Input, OnDestroy, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';

enum KeyboardKeys {
	Escape = 'Escape',
	Enter = 'Enter',
	Space = 'Space',
}

@Directive({
	selector: '[appFullTextTooltip]',
})
export class FullTextTooltipDirective implements OnDestroy {
	@Input() tooltipTitle = '';
	@Input() placement: TooltipPosition = 'top';
	@Input() hide: boolean = false;
	private tooltip: HTMLElement | null = null;
	private readonly OFFSET = 10;
	private readonly LAYOUT_OFFSET = 10;
	private readonly boundMousedownHandler: () => any;
	private readonly boundKeydownHandler: (event: KeyboardEvent) => any;

	constructor(private el: ElementRef, private renderer: Renderer2, @Inject(DOCUMENT) private document: Document) {
		this.boundMousedownHandler = this.onClick.bind(this);
		this.boundKeydownHandler = this.handleKeyDown.bind(this);
	}

	@HostListener('mouseenter') onMouseEnter(): void {
		if (this.hide) {
			return;
		}

		const element = this.el.nativeElement;
		if (!this.tooltip && element.scrollWidth > element.clientWidth) {
			this.show();
			this.attachHandlers();
		}
	}

	@HostListener('mouseleave') onMouseLeave(): void {
		if (this.tooltip) {
			this.remove();
			this.removeHandlers();
		}
	}

	private show(): void {
		this.create();
		this.setPosition();
		this.renderer.addClass(this.tooltip, 'full-text-tooltip-show');
	}

	private remove(): void {
		if (!this.tooltip) {
			return;
		}
		this.renderer.removeClass(this.tooltip, 'full-text-tooltip-show');
		this.renderer.removeChild(this.el.nativeElement, this.tooltip);
		this.tooltip = null;
	}

	private create(): void {
		const element = this.el.nativeElement;

		this.tooltipTitle = this.tooltipTitle || element.textContent;
		this.tooltip = this.renderer.createElement('span');
		const tooltipText = this.renderer.createText(this.tooltipTitle);

		this.renderer.addClass(this.tooltip, 'full-text-tooltip');

		// Add elements to the DOM
		this.renderer.appendChild(this.tooltip, tooltipText);
		this.renderer.appendChild(this.document.body, this.tooltip);
	}

	private setPosition(): void {
		const hostPos = this.el.nativeElement.getBoundingClientRect();
		const tooltipPos = (this.tooltip as HTMLElement).getBoundingClientRect();
		const scrollPos =
			window.pageYOffset || this.document.documentElement.scrollTop || this.document.body.scrollTop || 0;

		const layoutPosition = this.document.querySelector('app-default-layout');
		const leftLayoutPosition = layoutPosition?.getBoundingClientRect().left;
		const topLayoutPosition = layoutPosition?.getBoundingClientRect().top;

		let position = { top: 0, left: 0 };
		let finalPlacement: TooltipPosition = this.placement;

		if (this.placement === 'top') {
			position = this.calcPosition('top', hostPos, tooltipPos);
		}

		if (this.placement === 'bottom') {
			position = this.calcPosition('bottom', hostPos, tooltipPos);
		}

		if (this.placement === 'left') {
			position = this.calcPosition('left', hostPos, tooltipPos);
		}

		if (this.placement === 'right') {
			position = this.calcPosition('right', hostPos, tooltipPos);
		}

		// slightly move to the right
		if (typeof leftLayoutPosition === 'number' && position.left <= leftLayoutPosition) {
			position.left = leftLayoutPosition + this.LAYOUT_OFFSET;
		}

		// handle if tooltip doesn't fit to the top side of the screen - display from the bottom side
		if (typeof topLayoutPosition === 'number' && position.top <= topLayoutPosition) {
			position = this.calcPosition('bottom', hostPos, tooltipPos);
			finalPlacement = 'bottom';
		}

		this.renderer.addClass(this.tooltip, `full-text-tooltip-${finalPlacement}`);

		position.top = position.top + scrollPos;
		this.renderTooltip(this.tooltip, position);
	}

	private calcPosition(
		placement: string,
		hostPos: { bottom: number; left: number; width: number; top: number; height: number; right: number },
		tooltipPos: { width: number; height: number },
	): { top: number; left: number } {
		const position = { top: 0, left: 0 };

		switch (placement) {
			case 'bottom':
				position.top = hostPos.bottom + this.OFFSET;
				position.left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
				break;
			case 'left':
				position.top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
				position.left = hostPos.left - tooltipPos.width - this.OFFSET;
				break;
			case 'right':
				position.top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
				position.left = hostPos.right + this.OFFSET;
				break;
			default:
				position.top = hostPos.top - tooltipPos.height - this.OFFSET;
				position.left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
				break;
		}

		return position;
	}

	private renderTooltip(element: HTMLElement | null, position: { top: number; left: number }): void {
		this.renderer.setStyle(element, 'top', `${position.top}px`);
		this.renderer.setStyle(element, 'left', `${position.left}px`);
	}

	private onClick(): void {
		if (this.tooltip) {
			this.remove();
			this.removeHandlers();
		}
	}

	private handleKeyDown(event: KeyboardEvent): void {
		if (event.key in KeyboardKeys && this.tooltip) {
			this.remove();
			this.removeHandlers();
		}
	}

	private attachHandlers(): void {
		this.document.addEventListener('mousedown', this.boundMousedownHandler);
		this.document.addEventListener('keydown', this.boundKeydownHandler);
	}

	private removeHandlers(): void {
		this.document.removeEventListener('mousedown', this.boundMousedownHandler);
		this.document.removeEventListener('keydown', this.boundKeydownHandler);
	}

	public ngOnDestroy(): void {
		this.remove();
		this.removeHandlers();
	}
}
